import { WidgetSchema, WidgetSchemaWithContent } from '@/entities/widget';

import { CursorNextIcon, CursorPrevIcon } from '@/shared/assets';
import { cn } from '@/shared/lib';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselProps,
} from '@/shared/ui';

import { useInitSliderApi } from '../lib/hooks/useInitSliderApi';
import { useSelectedSnapDisplay } from '../lib/hooks/useSelectedSnapDisplay';
import Cursor from './cursor';
import { Pagination } from './pagination';
import SlidesList from './slides-list';

interface ISliderProps {
  slides: WidgetSchemaWithContent[];
  className?: string;
  activeSlide?: number;
  setActiveSlide?: (slide: number) => void;
  pagination?: boolean;
  cursor?: boolean;
  contentClassName?: string;
  disableInteractions?: boolean;
  onSlideClick?: (
    api: CarouselApi | null,
    slide: WidgetSchema,
    index: number,
  ) => void;
  opts?: CarouselProps['opts'];
}

export const Slider = ({
  slides,
  className,
  activeSlide,
  setActiveSlide,
  pagination,
  cursor,
  contentClassName,
  disableInteractions,
  onSlideClick,
  opts = {},
}: ISliderProps) => {
  const { setApi, api } = useInitSliderApi({ setActiveSlide });
  const { selectedSnap, snapCount } = useSelectedSnapDisplay(api);

  const isFirst = selectedSnap === 0;
  const isLast = selectedSnap === snapCount - 1;

  const onPaginate = (slide: number) => {
    api?.scrollTo(slide);
    setActiveSlide?.(slide);
  };

  const onPrevClick = () => {
    onPaginate(selectedSnap - 1);
  };

  const onNextClick = () => {
    onPaginate(selectedSnap + 1);
  };

  return (
    <Carousel
      setApi={setApi}
      opts={{
        align: 'center',
        loop: false,
        skipSnaps: false,
        containScroll: 'trimSnaps',
        dragFree: false,
        active: !disableInteractions,
        ...opts,
      }}
      className={cn('max-w-layout mx-auto w-full', className)}
    >
      {cursor && (
        <Cursor
          onClick={onPrevClick}
          cursorUrl={CursorPrevIcon}
          hidden={isFirst}
          position="left"
        />
      )}

      {cursor && (
        <Cursor
          onClick={onNextClick}
          cursorUrl={CursorNextIcon}
          hidden={isLast}
          position="right"
        />
      )}

      <CarouselContent className="gap-1" contentClassName={contentClassName}>
        <SlidesList
          slides={slides}
          onSlideClick={(slide, index) => {
            onSlideClick?.(api, slide, index);
          }}
        />
      </CarouselContent>

      {pagination && Number.isInteger(activeSlide) && (
        <Pagination
          className="mt-[22px]"
          slidesCount={snapCount}
          activeSlide={selectedSnap}
          onPaginate={onPaginate}
        />
      )}
    </Carousel>
  );
};
